/**
 * v1 accounts hub — profile card, sectioned action tiles, grouped list rows, social pills.
 */
import React, { useMemo } from 'react';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Linking, type StyleProp, type TextStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Clickable } from '../../../components/common/Clickable';
import { useAuthStore } from '../../../shared/auth/store';
import { logout } from '../../../shared/auth';
import { useTranslation, getSubscriptionPlanTitleFromMerged, useRtl, formatNumber } from '../../../shared/i18n';
import {
  useSubscriptionContext,
  getActiveMealDayProgressFromSubscription,
} from '../../../data/domains/subscription';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import {
  useCommunityFeatureEnabled,
  useConsultationsFeatureEnabled,
  useOnlineOrdersFeatureEnabled,
  useLoyaltyFeatureEnabled,
  useApiConfig,
  resolveOrganizationSocialLinks,
} from '../../../data/domains/config';
import { AnimatedSection } from '../../../components/common/AnimatedSection';
import { ContentCard } from '../../../components/common/ContentCard';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../../../components/common/ScreenHeader';
import { TAB_HEADER_EXPANDED_HEIGHT } from '../../../shared/theme/styles/headerTokens';
import { SCROLL_VIEW_PROPS } from '../../../shared/utils/scrollListPerf';
import { isDevHubClient } from '../../../shared/config/applications';
import { hexToRgba } from '../../../shared/theme/colorUtils';

const GRID_GAP = appTheme.spacing.md;
const TILE_ICON = 40;
const TILE_ICON_TITLE_GAP = appTheme.spacing.sm;
const TILE_TITLE_SUBTITLE_GAP = 4;
const TILE_TITLE_LINE_HEIGHT = 20;
const TILE_SUBTITLE_LINE_HEIGHT = 16;

type NavItem = {
  key: string;
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
  badge?: number;
  isDisabled?: boolean;
};

function AccountMenuItem({
  icon,
  title,
  subtitle,
  onPress,
  badge,
  colors,
  isDisabled,
  copy_text_style,
  disclosure_icon,
  show_divider,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  onPress: () => void;
  badge?: number;
  colors: ThemeColors;
  isDisabled?: boolean;
  copy_text_style: TextStyle;
  disclosure_icon: 'arrow-left' | 'arrow-right';
  show_divider?: boolean;
}) {
  return (
    <>
      {show_divider ? <View style={[itemStyles.divider, { backgroundColor: hexToRgba(colors.text, 0.08) as string }]} /> : null}
      <Clickable onPress={isDisabled ? undefined : onPress} disabled={isDisabled} accessibilityLabel={title} accessibilityRole="button">
        <View style={[itemStyles.row, isDisabled && itemStyles.rowDisabled]}>
          <View style={[itemStyles.iconWrap, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
            {isDisabled ? <ButtonLoader size={18} color={colors.primary} /> : <SvgIcon name={icon} size={20} color={colors.primary} />}
          </View>
          <View style={itemStyles.textWrap}>
            <Text style={[itemStyles.title, { color: colors.text }, copy_text_style]} numberOfLines={1}>
              {title}
            </Text>
            {subtitle ? (
              <Text style={[itemStyles.subtitle, { color: colors.textSecondary }, copy_text_style]} numberOfLines={2}>
                {subtitle}
              </Text>
            ) : null}
          </View>
          {badge !== undefined && badge > 0 ? (
            <View style={[itemStyles.badge, { backgroundColor: colors.error }]}>
              <Text style={[itemStyles.badgeText, { color: colors.onError }]}>{badge}</Text>
            </View>
          ) : null}
          <SvgIcon name={disclosure_icon} size={18} color={colors.textLight} />
        </View>
      </Clickable>
    </>
  );
}

const itemStyles = StyleSheet.create({
  divider: {
    height: StyleSheet.hairlineWidth,
    marginStart: appTheme.spacing.md + appTheme.sizes.iconButton + appTheme.spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.md,
    gap: appTheme.spacing.md,
  },
  rowDisabled: {
    opacity: 0.6,
  },
  iconWrap: {
    width: appTheme.sizes.iconButton,
    height: appTheme.sizes.iconButton,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textWrap: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: -0.2,
  },
  subtitle: {
    fontSize: 12.5,
    fontWeight: '500',
    lineHeight: 16,
  },
  badge: {
    borderRadius: appTheme.borderRadius.sm,
    paddingHorizontal: appTheme.spacing.sm,
    paddingVertical: appTheme.spacing.xxs,
    minWidth: 20,
    alignItems: 'center',
  },
  badgeText: {
    ...appTheme.typography.captionSmall,
    fontWeight: '600',
  },
});

function AccountActionTile({
  icon,
  title,
  subtitle,
  onPress,
  colors,
  badge,
  isDisabled,
  text_align,
  writing_direction,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  onPress: () => void;
  colors: ThemeColors;
  badge?: number;
  isDisabled?: boolean;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
}) {
  return (
    <Clickable
      onPress={isDisabled ? undefined : onPress}
      disabled={isDisabled}
      accessibilityLabel={title}
      accessibilityRole="button"
      style={[
        hubStyles.tile,
        {
          backgroundColor: colors.surface,
          shadowColor: colors.shadowColor,
        },
        isDisabled ? hubStyles.tileDisabled : null,
      ]}>
      <View style={hubStyles.tileTop}>
        <View style={[hubStyles.tileIcon, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
          {isDisabled ? <ButtonLoader size={18} color={colors.primary} /> : <SvgIcon name={icon} size={20} color={colors.primary} />}
        </View>
        {badge != null && badge > 0 ? (
          <View style={[hubStyles.tileBadge, { backgroundColor: colors.error }]}>
            <Text style={[hubStyles.tileBadgeText, { color: colors.onError }]}>{badge}</Text>
          </View>
        ) : (
          <View style={hubStyles.tileBadgeSpacer} />
        )}
      </View>
      <View style={hubStyles.tileCopy}>
        <Text
          style={[hubStyles.tileTitle, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }]}
          numberOfLines={2}>
          {title}
        </Text>
        {subtitle ? (
          <Text
            style={[hubStyles.tileSubtitle, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }]}
            numberOfLines={2}>
            {subtitle}
          </Text>
        ) : null}
      </View>
    </Clickable>
  );
}

const hubStyles = StyleSheet.create({
  tile: {
    flex: 1,
    width: '100%',
    borderRadius: 18,
    padding: appTheme.spacing.md,
    gap: TILE_ICON_TITLE_GAP,
    overflow: 'visible',
    ...appTheme.shadows.xs,
  },
  tileDisabled: {
    opacity: 0.6,
  },
  tileTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tileIcon: {
    width: TILE_ICON,
    height: TILE_ICON,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileBadge: {
    borderRadius: 999,
    paddingHorizontal: appTheme.spacing.sm,
    paddingVertical: 2,
    minWidth: 18,
    alignItems: 'center',
  },
  tileBadgeSpacer: {
    width: 18,
    height: 18,
  },
  tileBadgeText: {
    ...appTheme.typography.captionSmall,
    fontWeight: '800',
  },
  tileCopy: {
    gap: TILE_TITLE_SUBTITLE_GAP,
    minWidth: 0,
  },
  tileTitle: {
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: -0.2,
    lineHeight: TILE_TITLE_LINE_HEIGHT,
  },
  tileSubtitle: {
    fontSize: 12.5,
    fontWeight: '500',
    lineHeight: TILE_SUBTITLE_LINE_HEIGHT,
  },
});

function SectionBlock({
  label,
  label_style,
  children,
}: {
  label: string;
  label_style: StyleProp<TextStyle>;
  children: React.ReactNode;
}) {
  return (
    <View style={layoutStyles.section}>
      <Text style={label_style}>{label}</Text>
      {children}
    </View>
  );
}

function ActionTileGrid({
  items,
  colors,
  text_align,
  writing_direction,
}: {
  items: NavItem[];
  colors: ThemeColors;
  text_align: 'left' | 'right';
  writing_direction: 'ltr' | 'rtl';
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <View style={layoutStyles.gridWrap}>
      {items.map(item => (
        <View key={item.key} style={layoutStyles.gridCell}>
          <AccountActionTile
            colors={colors}
            icon={item.icon}
            title={item.title}
            subtitle={item.subtitle}
            onPress={item.onPress}
            badge={item.badge}
            isDisabled={item.isDisabled}
            text_align={text_align}
            writing_direction={writing_direction}
          />
        </View>
      ))}
    </View>
  );
}

function GroupedListCard({
  items,
  colors,
  menu_item_rtl,
}: {
  items: NavItem[];
  colors: ThemeColors;
  menu_item_rtl: {
    copy_text_style: TextStyle;
    disclosure_icon: 'arrow-left' | 'arrow-right';
  };
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <ContentCard
      variant="default"
      borderless
      padding="none"
      style={[layoutStyles.groupCard, { backgroundColor: colors.surface, shadowColor: colors.shadowColor, ...appTheme.shadows.xs }]}>
      {items.map((item, index) => (
        <AccountMenuItem
          key={item.key}
          colors={colors}
          icon={item.icon}
          title={item.title}
          subtitle={item.subtitle}
          onPress={item.onPress}
          badge={item.badge}
          isDisabled={item.isDisabled}
          show_divider={index > 0}
          {...menu_item_rtl}
        />
      ))}
    </ContentCard>
  );
}

const layoutStyles = StyleSheet.create({
  section: {
    gap: appTheme.spacing.sm,
  },
  gridWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: GRID_GAP,
  },
  gridCell: {
    flexBasis: '47%',
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '50%',
  },
  groupCard: {
    padding: 0,
    overflow: 'hidden',
    borderRadius: 18,
  },
});

function createAccountsScreenStyles(c: ThemeColors) {
  return StyleSheet.create({
    scroll: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: appTheme.spacing.lg,
      gap: appTheme.spacing.lg,
    },
    headerLogoutBtn: {
      width: appTheme.sizes.iconButton,
      height: appTheme.sizes.iconButton,
      borderRadius: appTheme.borderRadius.round,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sectionTitle: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.8,
      color: c.textLight,
      paddingHorizontal: appTheme.spacing.xxs,
      textTransform: 'uppercase',
    },
    profileCard: {
      padding: 0,
      overflow: 'visible',
      borderRadius: 20,
    },
    profileInner: {
      padding: appTheme.spacing.lg,
      gap: appTheme.spacing.md,
    },
    profileTopRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.md,
    },
    profileIdentity: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
      gap: appTheme.spacing.md,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileTextCol: {
      flex: 1,
      minWidth: 0,
      gap: 3,
    },
    profileName: {
      fontSize: 17,
      fontWeight: '800',
      letterSpacing: -0.3,
    },
    profileMeta: {
      fontSize: 13,
      fontWeight: '500',
      lineHeight: 17,
    },
    planPill: {
      flexShrink: 0,
      alignSelf: 'flex-start',
      borderRadius: 999,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs + 1,
    },
    planPillText: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.2,
    },
    progressTrack: {
      height: 6,
      borderRadius: 999,
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      borderRadius: 999,
    },
    progressFillRtl: {
      alignSelf: 'flex-end',
    },
    profileDateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    socialCard: {
      padding: appTheme.spacing.md,
      borderRadius: 18,
      overflow: 'hidden',
    },
    socialRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.md,
    },
    /** Equal columns — `flexBasis: 0` avoids content-sized overflow from long labels. */
    socialPillCell: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
    },
    socialPill: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.xs,
      borderRadius: 999,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.sm,
    },
    socialPillText: {
      fontSize: 11,
      fontWeight: '700',
      flexShrink: 1,
      minWidth: 0,
      textAlign: 'center',
    },
    footer: {
      alignItems: 'center',
      paddingTop: appTheme.spacing.xs,
    },
    footerText: {
      ...appTheme.typography.captionSmall,
      color: c.textSecondary,
    },
  });
}

export const AccountsScreenSet1: React.FC = () => {
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const rtl = useRtl();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAccountsScreenStyles(colors), [colors]);
  const user = useAuthStore(s => s.user);
  const { activeSubscriptions, availableDates, subscriptions, dayConfigMap, config } = useSubscriptionContext();
  const { organization } = useApiConfig();
  const social_links = useMemo(() => resolveOrganizationSocialLinks(), [organization]);
  const activeSub = activeSubscriptions[0];
  const meal_progress = useMemo(
    () =>
      activeSub
        ? getActiveMealDayProgressFromSubscription(activeSub, availableDates, subscriptions, dayConfigMap, config)
        : null,
    [activeSub, availableDates, subscriptions, dayConfigMap, config],
  );
  const daysLeft = meal_progress?.daysLeft ?? 0;
  const pausedDaysLeft = meal_progress?.pausedDaysLeft ?? 0;
  const progress_pct = Math.max(0, Math.min(100, meal_progress?.progressPercentage ?? 0));

  const handleLogout = async () => {
    await logout();
  };

  const openSocialLink = (url: string) => {
    Linking.openURL(url).catch(() => {});
  };

  const subscription_title = activeSub ? getSubscriptionPlanTitleFromMerged(activeSub, t('account.activePlan')) : null;
  const content_top = useHeaderTopPadding(TAB_HEADER_EXPANDED_HEIGHT, appTheme.spacing.md);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const padding_bottom = insets.bottom + appTheme.spacing.lg;

  const profile_name = user?.name || t('account.title');
  const plan_label = activeSub ? subscription_title : t('account.browsePlans');
  const plan_subtitle = activeSub
    ? pausedDaysLeft > 0
      ? t('calendar.deliveryDaysRemainingPaused', { remaining: daysLeft, paused: pausedDaysLeft })
      : `${formatNumber(daysLeft)} ${t('account.daysLeft')}`
    : t('account.exploreSubscriptionOptions');
  const show_community = useCommunityFeatureEnabled();
  const show_consultations = useConsultationsFeatureEnabled();
  const show_online_orders = useOnlineOrdersFeatureEnabled();
  const show_loyalty = useLoyaltyFeatureEnabled();

  const menu_item_rtl = {
    copy_text_style: rtl.textStyle({ align: 'start', writing: 'locale', stretch: false }),
    disclosure_icon: rtl.trailingRowDisclosureChevron,
  };

  const tile_rtl = {
    text_align: rtl.textAlign as 'left' | 'right',
    writing_direction: rtl.writingDirection,
  };

  const section_title_style: StyleProp<TextStyle> = [
    styles.sectionTitle,
    { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection },
  ];

  const subscription_tiles: NavItem[] = useMemo(() => {
    const rows: NavItem[] = [
      {
        key: 'my-subscriptions',
        icon: 'menu',
        title: t('account.mySubscriptions'),
        subtitle: t('account.manageYourPlans'),
        onPress: () => navigation.navigate('MySubscriptions'),
      },
      {
        key: 'browse-plans',
        icon: 'list',
        title: t('account.browsePlans'),
        subtitle: t('account.exploreSubscriptionOptions'),
        onPress: () => navigation.navigate('SubscriptionList'),
      },
    ];
    if (show_loyalty) {
      rows.push({
        key: 'loyalty',
        icon: 'gift',
        title: t('account.loyaltyRewards'),
        subtitle: t('account.viewYourPoints'),
        onPress: () => navigation.navigate('Loyalty'),
      });
    }
    return rows;
  }, [t, navigation, show_loyalty]);

  const account_rows: NavItem[] = useMemo(() => {
    const rows: NavItem[] = [
      {
        key: 'profile',
        icon: 'user',
        title: t('account.profile'),
        subtitle: t('account.editYourInformation'),
        onPress: () => navigation.navigate('Profile'),
      },
      {
        key: 'addresses',
        icon: 'map',
        title: t('account.addresses'),
        subtitle: t('account.manageDeliveryAddresses'),
        onPress: () => navigation.navigate('AddressLists'),
      },
      {
        key: 'settings',
        icon: 'settings',
        title: t('account.settings'),
        subtitle: t('account.languageAndPreferences'),
        onPress: () => navigation.navigate('Settings'),
      },
      {
        key: 'allergies',
        icon: 'list',
        title: t('account.allergiesAndDislikes'),
        subtitle: t('account.manageAllergiesDislikes'),
        onPress: () => navigation.navigate('AllergiesDislikes'),
      },
    ];
    return rows;
  }, [t, navigation]);

  const orders_rows: NavItem[] = useMemo(() => {
    if (!show_online_orders) {
      return [];
    }
    return [
      {
        key: 'order-history',
        icon: 'time',
        title: t('account.orderHistory'),
        subtitle: t('account.viewPastOrders'),
        onPress: () => navigation.navigate('OrderHistory'),
      },
    ];
  }, [t, navigation, show_online_orders]);

  const support_rows: NavItem[] = useMemo(() => {
    const rows: NavItem[] = [];
    if (show_consultations) {
      rows.push({
        key: 'consultation',
        icon: 'chat',
        title: t('account.consultation'),
        subtitle: t('account.bookASession'),
        onPress: () => navigation.navigate('Consultation'),
      });
    }
    rows.push({
      key: 'notifications',
      icon: 'bell',
      title: t('account.notifications'),
      subtitle: t('account.manageNotifications'),
      onPress: () => navigation.navigate('Notifications'),
    });
    if (show_community) {
      rows.push({
        key: 'community',
        icon: 'chat',
        title: t('account.community'),
        subtitle: t('account.communitySubtitle'),
        onPress: () => navigation.navigate('Community'),
      });
    }
    return rows;
  }, [t, navigation, show_consultations, show_community]);

  const developer_rows: NavItem[] = useMemo(() => {
    if (!isDevHubClient()) {
      return [];
    }
    return [
      {
        key: 'dev-tools',
        icon: 'settings',
        title: 'Dev Tools',
        subtitle: 'UI tier, API target, perf',
        onPress: () => navigation.navigate('DevTools'),
      },
    ];
  }, [navigation]);

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <ScreenHeader
        title={t('account.title')}
        subtitle={profile_name}
        collapseOnScroll
        scrollY={scrollY}
        expandedHeight={TAB_HEADER_EXPANDED_HEIGHT}
        textAlign={rtl.textAlign}
        rightAction={
          <Clickable onPress={handleLogout} accessibilityLabel={t('account.logout')} accessibilityRole="button">
            <View style={styles.headerLogoutBtn}>
              <SvgIcon name="logout" size={24} color={colors.text} />
            </View>
          </Clickable>
        }
      />

      <Animated.ScrollView
        {...SCROLL_VIEW_PROPS}
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: content_top, paddingBottom: padding_bottom }]}
        keyboardShouldPersistTaps="handled">
        <AnimatedSection index={0}>
          <ContentCard
            variant="default"
            borderless
            padding="lg"
            style={[
              styles.profileCard,
              {
                backgroundColor: colors.surface,
                shadowColor: colors.shadowColor,
                ...appTheme.shadows.xs,
              },
            ]}>
            <View style={styles.profileInner}>
              <View style={styles.profileTopRow}>
                <View style={styles.profileIdentity}>
                  <View style={[styles.avatar, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
                    <SvgIcon name="user" size={22} color={colors.primary} />
                  </View>
                  <View style={styles.profileTextCol}>
                    <Text
                      style={[styles.profileName, { color: colors.text, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                      numberOfLines={1}>
                      {profile_name}
                    </Text>
                    <Text
                      style={[styles.profileMeta, { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                      numberOfLines={1}>
                      {activeSub ? t('account.activePlan') : t('account.manageYourPlans')}
                    </Text>
                  </View>
                </View>

                <Clickable
                  onPress={() => navigation.navigate(activeSub ? 'MySubscriptions' : 'SubscriptionList')}
                  accessibilityLabel={plan_label ?? t('account.mySubscriptions')}
                  accessibilityRole="button">
                  <View style={[styles.planPill, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
                    <Text
                      style={[styles.planPillText, { color: colors.primary, writingDirection: rtl.writingDirection }]}
                      numberOfLines={1}>
                      {activeSub ? plan_subtitle : t('account.browsePlans')}
                    </Text>
                  </View>
                </Clickable>
              </View>

              {activeSub ? (
                <>
                  <View style={styles.profileDateRow}>
                    <SvgIcon name="calendar" size={16} color={colors.textLight} />
                    <Text
                      style={[styles.profileMeta, { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection, flex: 1 }]}
                      numberOfLines={1}>
                      {activeSub.start_date} – {activeSub.end_date}
                    </Text>
                  </View>
                  <View style={[styles.progressTrack, { backgroundColor: hexToRgba(colors.text, 0.08) as string }]}>
                    <Animated.View
                      style={[
                        styles.progressFill,
                        { width: `${progress_pct}%`, backgroundColor: colors.primary },
                        rtl.isRTL ? styles.progressFillRtl : null,
                      ]}
                    />
                  </View>
                </>
              ) : (
                <Text
                  style={[styles.profileMeta, { color: colors.textSecondary, textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]}
                  numberOfLines={2}>
                  {t('account.exploreSubscriptionOptions')}
                </Text>
              )}
            </View>
          </ContentCard>
        </AnimatedSection>

        <AnimatedSection index={1}>
          <SectionBlock label={t('account.subscriptionSection')} label_style={section_title_style}>
            <ActionTileGrid items={subscription_tiles} colors={colors} {...tile_rtl} />
          </SectionBlock>
        </AnimatedSection>

        <AnimatedSection index={2}>
          <SectionBlock label={t('account.accountSection')} label_style={section_title_style}>
            <GroupedListCard items={account_rows} colors={colors} menu_item_rtl={menu_item_rtl} />
          </SectionBlock>
        </AnimatedSection>

        {orders_rows.length > 0 ? (
          <AnimatedSection index={3}>
            <SectionBlock label={t('account.ordersSection')} label_style={section_title_style}>
              <GroupedListCard items={orders_rows} colors={colors} menu_item_rtl={menu_item_rtl} />
            </SectionBlock>
          </AnimatedSection>
        ) : null}

        {support_rows.length > 0 ? (
          <AnimatedSection index={4}>
            <SectionBlock label={t('account.supportSection')} label_style={section_title_style}>
              <GroupedListCard items={support_rows} colors={colors} menu_item_rtl={menu_item_rtl} />
            </SectionBlock>
          </AnimatedSection>
        ) : null}

        {developer_rows.length > 0 ? (
          <AnimatedSection index={5}>
            <SectionBlock label={t('account.developerSection')} label_style={section_title_style}>
              <GroupedListCard items={developer_rows} colors={colors} menu_item_rtl={menu_item_rtl} />
            </SectionBlock>
          </AnimatedSection>
        ) : null}

        {social_links.length > 0 ? (
          <AnimatedSection index={6}>
            <ContentCard
              variant="default"
              borderless
              padding="none"
              style={[styles.socialCard, { backgroundColor: colors.surface, shadowColor: colors.shadowColor, ...appTheme.shadows.xs }]}>
              <View style={[styles.socialRow, rtl.layoutLtrStyle]}>
                {social_links.map(link => (
                  <View key={link.key} style={styles.socialPillCell}>
                    <Clickable
                      onPress={() => openSocialLink(link.url)}
                      accessibilityLabel={link.label}
                      accessibilityRole="link">
                      <View style={[styles.socialPill, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
                        <SvgIcon
                          name={link.icon}
                          size={16}
                          color={link.key === 'whatsapp' ? '#25D366' : link.key === 'instagram' ? '#E4405F' : '#FF0000'}
                        />
                        <Text style={[styles.socialPillText, { color: colors.text }]} numberOfLines={1} ellipsizeMode="tail">
                          {link.label}
                        </Text>
                      </View>
                    </Clickable>
                  </View>
                ))}
              </View>
            </ContentCard>
          </AnimatedSection>
        ) : null}

        <View style={[styles.footer, { paddingBottom: insets.bottom + appTheme.spacing.sm }]}>
          <Text style={[styles.footerText, { color: colors.textLight }]}>{t('account.version')} 1.0.0</Text>
        </View>
      </Animated.ScrollView>
    </View>
  );
};

export default AccountsScreenSet1;
